import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Activity, TrendingUp, Calendar, Flame, Award, AlertCircle, Heart, Phone, Settings as SettingsIcon, Lock, Mail } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { analytics } from "@/instrument";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SIDE_EFFECT_CHIPS = [
  { id: "nausea", label: "Nausea" },
  { id: "fatigue", label: "Fatigue" },
  { id: "gi_upset", label: "GI upset" },
  { id: "headache", label: "Headache" },
  { id: "dizziness", label: "Dizziness" },
  { id: "injection_site_reaction", label: "Injection site reaction" },
  { id: "decreased_appetite", label: "Decreased appetite" },
  { id: "constipation", label: "Constipation" },
  { id: "diarrhea", label: "Diarrhea" },
  { id: "other", label: "Other" },
];

/**
 * Patient wellness card — weekly weigh-in, streak tracking, side-effect log,
 * 5-week stackable $10 coupon reward.
 */
export default function WellnessCard() {
  const [profile, setProfile] = useState(null);
  const [chart, setChart] = useState({ points: [] });
  const [showSettings, setShowSettings] = useState(false);
  const [showWeight, setShowWeight] = useState(false);
  const [showSideEffect, setShowSideEffect] = useState(false);
  const [escalation, setEscalation] = useState(null); // { needs_escalation, chat_available }
  const [weightInput, setWeightInput] = useState("");
  const [weightNote, setWeightNote] = useState("");
  const [chips, setChips] = useState([]);
  const [severity, setSeverity] = useState("mild");
  const [seNotes, setSeNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportReason, setSupportReason] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);

  const submitSupportRequest = async () => {
    setSupportSubmitting(true);
    try {
      await API.post("/wellness/contact-support-unlock", { reason: supportReason });
      toast.success("Your request has been sent. Support will reach out shortly.");
      setShowSupportModal(false);
      setSupportReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setSupportSubmitting(false);
    }
  };
  const [dowInput, setDowInput] = useState(0);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: c }] = await Promise.all([
        API.get("/wellness/profile"),
        API.get("/wellness/chart?weeks=12"),
      ]);
      setProfile(p);
      setChart(c);
      setDowInput(p.weigh_in_dow ?? 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDow = async () => {
    setSubmitting(true);
    try {
      const { data } = await API.put("/wellness/profile", { weigh_in_dow: dowInput });
      setProfile(data);
      toast.success(`Weigh-in day locked to ${DAYS[dowInput]}`);
      setShowSettings(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.code === "DOW_LOCKED")
        ? detail.message
        : (typeof detail === "string" ? detail : "Failed to save");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const submitWeight = async () => {
    const w = parseFloat(weightInput);
    if (!w || w < 20 || w > 1500) { toast.error("Enter a valid weight in lbs"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/wellness/weight", { weight_lbs: w, notes: weightNote.trim() || undefined });
      analytics.capture("weight_logged", { streak: data?.profile?.streak_count || 0, coupon_awarded: !!data.coupon_awarded });
      if (data.coupon_awarded) {
        toast.success(data.streak_message, { duration: 8000 });
      } else {
        toast.success(data.streak_message);
      }
      setProfile(data.profile);
      setWeightInput(""); setWeightNote("");
      setShowWeight(false);
      load(); // refresh chart
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log weight");
    } finally { setSubmitting(false); }
  };

  const toggleChip = (id) => setChips((prev) => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  const submitSideEffect = async () => {
    if (chips.length === 0) { toast.error("Select at least one symptom"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post("/wellness/side-effect", { chips, severity, notes: seNotes.trim() || undefined });
      analytics.capture("side_effect_logged", { severity, chip_count: chips.length });
      setShowSideEffect(false);
      setChips([]); setSeNotes(""); setSeverity("mild");
      if (data.needs_escalation) {
        setEscalation({ needs_escalation: true, chat_available: data.chat_available });
      } else {
        toast.success("Side effect logged. Your prescriber will see this in your chart.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally { setSubmitting(false); }
  };

  if (!profile) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 animate-pulse h-48" data-testid="wellness-card-loading" />
    );
  }

  const streakPct = Math.min(100, (profile.current_streak / profile.streak_target) * 100);

  return (
    <div className="h-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6" data-testid="wellness-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 dark:text-white text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>Wellness Tracker</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {profile.weigh_in_dow_label
                ? `Weekly weigh-in every ${profile.weigh_in_dow_label}`
                : "Pick your weekly weigh-in day to start"}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} data-testid="wellness-settings-btn" title="Settings">
          <SettingsIcon className="w-4 h-4" />
        </Button>
      </div>

      {profile.weigh_in_dow !== null && (
        <>
          {/* Streak progress */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 mb-4" data-testid="wellness-streak">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Flame className={`w-4 h-4 ${profile.current_streak > 0 ? "text-orange-500" : "text-slate-400"}`} />
                <span className="text-sm font-semibold text-slate-900 dark:text-white">
                  Streak: {profile.current_streak}/{profile.streak_target} weeks
                </span>
              </div>
              {profile.coupons_earned_total > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-700 bg-white border border-purple-200 rounded-full px-2 py-0.5" data-testid="wellness-coupons-earned">
                  <Award className="w-3 h-3" /> {profile.coupons_earned_total} earned
                </span>
              )}
            </div>
            <div className="w-full bg-white/60 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                style={{ width: `${streakPct}%` }}
                data-testid="wellness-streak-bar"
              />
            </div>
            <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-2">
              5 weeks in a row = <strong>$10 off coupon</strong> (stackable with subscription discount). Max {profile.coupon_cap_12mo} coupons / 12 months — you&apos;ve earned {profile.coupons_in_last_12mo}.
            </p>
          </div>

          {/* Weight chart */}
          {chart.points.length > 0 && (
            <div className="mb-4" data-testid="wellness-chart">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400 inline-flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> 12-week weight trend
                </span>
                <span className="text-xs text-slate-400">{chart.points.length} log{chart.points.length === 1 ? "" : "s"}</span>
              </div>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={chart.points} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="logged_date" tick={{ fontSize: 10 }} hide />
                  <YAxis dataKey="weight_lbs" tick={{ fontSize: 10 }} domain={["auto", "auto"]} width={40} />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    labelFormatter={(v) => `Date: ${v}`}
                    formatter={(v) => [`${v} lbs`, "Weight"]}
                  />
                  {chart.points.length > 1 && (
                    <ReferenceLine y={chart.points[0].weight_lbs} stroke="#a78bfa" strokeDasharray="4 4" />
                  )}
                  <Line
                    type="monotone"
                    dataKey="weight_lbs"
                    stroke="#7c3aed"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "#7c3aed" }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => { setWeightInput(""); setWeightNote(""); setShowWeight(true); }}
              className={`rounded-xl text-sm ${profile.is_weigh_in_day && !profile.already_logged_today ? "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white animate-pulse" : "bg-purple-600 hover:bg-purple-700 text-white"}`}
              data-testid="wellness-log-weight-btn"
            >
              <TrendingUp className="w-3.5 h-3.5 mr-1" />
              {profile.already_logged_today ? "Logged today ✓" : profile.is_weigh_in_day ? "Log weight (counts!)" : "Log weight"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setChips([]); setSeNotes(""); setSeverity("mild"); setShowSideEffect(true); }}
              className="rounded-xl text-sm border-rose-200 text-rose-700 hover:bg-rose-50"
              data-testid="wellness-log-side-effect-btn"
            >
              <Heart className="w-3.5 h-3.5 mr-1" /> Log side effect
            </Button>
          </div>

          {profile.is_weigh_in_day && !profile.already_logged_today && (
            <p className="text-[11px] text-purple-700 mt-2 inline-flex items-center gap-1" data-testid="wellness-todays-day-banner">
              <Calendar className="w-3 h-3" /> Today is your weigh-in day! Log to keep your streak alive.
            </p>
          )}
        </>
      )}

      {profile.weigh_in_dow === null && (
        <Button onClick={() => setShowSettings(true)} className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white" data-testid="wellness-pick-day-btn">
          <Calendar className="w-4 h-4 mr-2" /> Pick my weigh-in day
        </Button>
      )}

      {/* Settings dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md" data-testid="wellness-settings-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Wellness Settings</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Weigh-in day of the week</Label>
              {profile.dow_locked ? (
                /* Locked — UX:
                   1. Banner showing the chosen day.
                   2. "Switch to a different day" → if the patient hasn't
                      used their one-time freebie switch, the day picker
                      reappears below for one final pick. Otherwise, this
                      button is hidden.
                   3. "Need help? Contact our support team" → fires a
                      manual-unlock email so support can clear the lock
                      without breaking the streak. (We deliberately don't
                      mention the silent grace miss — undocumented per spec.) */
                <div className="mt-2 space-y-3" data-testid="wellness-dow-locked">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Lock className="w-4 h-4 text-emerald-600" />
                      <p className="text-sm font-bold text-emerald-800">Locked to {profile.weigh_in_dow_label}</p>
                    </div>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                      To keep things fair, your weigh-in day is locked once you choose it.
                    </p>
                  </div>

                  {!profile.free_switch_used && (
                    <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
                      <p className="text-xs text-purple-800 mb-2">
                        <strong>One-time switch available.</strong> Pick a new day below — your streak stays intact.
                      </p>
                      <div className="grid grid-cols-7 gap-1.5">
                        {DAYS.map((d, i) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setDowInput(i)}
                            className={`text-xs rounded-lg py-2 transition-colors ${dowInput === i ? "bg-purple-600 text-white font-bold" : "bg-white text-purple-700 hover:bg-purple-100 border border-purple-200"}`}
                            data-testid={`wellness-dow-${i}`}
                          >
                            {d.slice(0, 3)}
                          </button>
                        ))}
                      </div>
                      <Button
                        onClick={saveDow}
                        disabled={submitting || dowInput === profile.weigh_in_dow}
                        className="mt-3 w-full bg-purple-600 hover:bg-purple-700 text-white text-xs"
                        data-testid="wellness-use-free-switch"
                      >
                        {submitting ? "Saving..." : `Use my one-time switch ${dowInput !== null && dowInput !== undefined ? `→ ${DAYS[dowInput]}` : ""}`}
                      </Button>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setShowSupportModal(true)}
                    className="w-full text-xs font-medium text-slate-500 hover:text-purple-600 underline-offset-4 hover:underline transition-colors"
                    data-testid="wellness-contact-support"
                  >
                    Need help? Contact support to unlock
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mt-1 mb-2">
                    Pick the day each week you&apos;ll log your weight. <strong>Logging is only counted on this day</strong>.
                    Once you save, your day will be <strong>locked</strong> and only unlocks automatically the day after a missed weigh-in.
                  </p>
                  <div className="grid grid-cols-7 gap-1.5">
                    {DAYS.map((d, i) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDowInput(i)}
                        className={`text-xs rounded-lg py-2 transition-colors ${dowInput === i ? "bg-purple-600 text-white font-bold" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-purple-100"}`}
                        data-testid={`wellness-dow-${i}`}
                      >
                        {d.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {profile.coupons_earned_log?.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Coupons earned</Label>
                <ul className="mt-2 space-y-1.5 max-h-32 overflow-y-auto" data-testid="wellness-coupons-log">
                  {profile.coupons_earned_log.map((c) => (
                    <li key={c.coupon_id} className="text-xs flex items-center justify-between bg-purple-50 rounded px-2 py-1.5 border border-purple-100">
                      <span className="font-mono font-bold text-purple-700">{c.code}</span>
                      <span className="text-slate-500">${c.amount} · {c.earned_at?.slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>{profile.dow_locked ? "Close" : "Cancel"}</Button>
            {!profile.dow_locked && (
              <Button onClick={saveDow} disabled={submitting} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="wellness-settings-save">
                {submitting ? "Saving..." : "Save & lock day"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weight log dialog */}
      <Dialog open={showWeight} onOpenChange={setShowWeight}>
        <DialogContent className="max-w-md" data-testid="wellness-weight-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Log your weight</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Weight (lbs)</Label>
              <Input
                type="number" step="0.1" inputMode="decimal" autoFocus
                value={weightInput}
                onChange={(e) => setWeightInput(e.target.value)}
                placeholder="e.g. 182.5"
                className="mt-1 rounded-xl"
                data-testid="wellness-weight-input"
              />
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Input
                value={weightNote}
                onChange={(e) => setWeightNote(e.target.value)}
                placeholder="e.g. weighed first thing this morning"
                maxLength={500}
                className="mt-1 rounded-xl"
                data-testid="wellness-weight-notes"
              />
            </div>
            {profile.is_weigh_in_day && !profile.already_logged_today ? (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-2 inline-flex items-start gap-1.5">
                <Flame className="w-3.5 h-3.5 mt-0.5 text-orange-500 flex-shrink-0" />
                <span>Today is your <strong>{profile.weigh_in_dow_label}</strong> weigh-in! This log will count toward your streak.</span>
              </p>
            ) : !profile.is_weigh_in_day ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 inline-flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>Logs on non-weigh-in days are saved but don&apos;t count toward your streak. Your weigh-in day is <strong>{profile.weigh_in_dow_label}</strong>.</span>
              </p>
            ) : (
              <p className="text-xs text-slate-500">You&apos;ve already logged today — your streak is unchanged.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWeight(false)}>Cancel</Button>
            <Button onClick={submitWeight} disabled={submitting} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="wellness-weight-save">
              {submitting ? "Logging..." : "Log weight"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Side-effect log dialog */}
      <Dialog open={showSideEffect} onOpenChange={setShowSideEffect}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="wellness-side-effect-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Log a side effect</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">What are you experiencing? <span className="text-slate-400 font-normal">(tap all that apply)</span></Label>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {SIDE_EFFECT_CHIPS.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleChip(c.id)}
                    className={`text-xs rounded-full py-1.5 px-3 border transition-colors ${chips.includes(c.id) ? "bg-rose-600 text-white border-rose-600" : "bg-white border-slate-200 text-slate-700 hover:border-rose-200"}`}
                    data-testid={`wellness-chip-${c.id}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Severity</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-2">
                {[
                  { id: "mild", label: "Mild", cls: "border-emerald-200 text-emerald-700", active: "bg-emerald-600 text-white border-emerald-600" },
                  { id: "moderate", label: "Moderate", cls: "border-amber-200 text-amber-700", active: "bg-amber-600 text-white border-amber-600" },
                  { id: "severe", label: "Severe", cls: "border-rose-200 text-rose-700", active: "bg-rose-600 text-white border-rose-600" },
                ].map(s => (
                  <button
                    key={s.id} type="button"
                    onClick={() => setSeverity(s.id)}
                    className={`text-sm font-medium rounded-lg py-2 border transition-colors ${severity === s.id ? s.active : `bg-white ${s.cls} hover:bg-slate-50`}`}
                    data-testid={`wellness-severity-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Input
                value={seNotes}
                onChange={(e) => setSeNotes(e.target.value)}
                placeholder="e.g. started 4 hours after my injection"
                maxLength={1000}
                className="mt-1 rounded-xl"
                data-testid="wellness-side-effect-notes"
              />
            </div>
            <p className="text-xs text-slate-500">Your prescriber can see this log in your chart at your next visit. <strong>If severe, we&apos;ll connect you with help right away.</strong></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSideEffect(false)}>Cancel</Button>
            <Button onClick={submitSideEffect} disabled={submitting || chips.length === 0} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="wellness-side-effect-save">
              {submitting ? "Logging..." : "Log side effect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Severity escalation dialog */}
      <Dialog open={!!escalation} onOpenChange={() => setEscalation(null)}>
        <DialogContent className="max-w-md" data-testid="wellness-escalation-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <AlertCircle className="w-5 h-5" /> Severe symptoms — let&apos;s get you help
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-rose-700 font-semibold bg-rose-50 border border-rose-200 rounded-lg p-3">
              ⚠️ <strong>Medical emergency?</strong> Call <a href="tel:911" className="underline">911</a> immediately. Do not wait for a chat reply.
            </p>
            {escalation?.chat_available ? (
              <>
                <p>Your symptoms have been logged. Our clinical team is online — let&apos;s start a chat right now so they can help.</p>
                <Button
                  onClick={() => {
                    setEscalation(null);
                    // Trigger chat widget with clinical category preset
                    window.dispatchEvent(new CustomEvent("glpflo:open-chat", { detail: { category: "clinical", prefill: "I just logged a severe side effect — please review my chart." } }));
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
                  data-testid="wellness-escalation-chat-btn"
                >
                  <Heart className="w-4 h-4 mr-2" /> Start chat with clinical team
                </Button>
              </>
            ) : (
              <>
                <p>Your symptoms have been logged. Our chat team isn&apos;t online right now — for severe symptoms, please call us directly:</p>
                <a
                  href="tel:+17275162142"
                  className="block bg-purple-600 hover:bg-purple-700 text-white rounded-xl p-4 text-center font-bold text-lg transition-colors"
                  data-testid="wellness-escalation-phone-btn"
                >
                  <Phone className="w-5 h-5 inline mr-2" /> (727) 516-2142
                </a>
                <p className="text-xs text-slate-500">After hours, leave a voicemail and a clinician will call you back. <strong>For any medical emergency, call 911.</strong></p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalation(null)} data-testid="wellness-escalation-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact support to unlock weigh-in day. Patient writes a short
          reason; backend emails the support team and audit-logs the
          request. Manual unlock is performed by staff from the patient
          detail panel — preserves the streak. */}
      <Dialog open={showSupportModal} onOpenChange={setShowSupportModal}>
        <DialogContent className="max-w-md" data-testid="wellness-support-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Mail className="w-5 h-5 text-purple-600" /> Request a manual unlock
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Tell our team why you need to switch days and we&apos;ll unlock your weigh-in
              without breaking your streak.
            </p>
            <div>
              <Label className="text-sm font-medium">What&apos;s going on?</Label>
              <Textarea
                value={supportReason}
                onChange={(e) => setSupportReason(e.target.value)}
                placeholder="e.g. New work schedule, traveling next week, hospitalized…"
                className="mt-1 rounded-xl min-h-[100px]"
                data-testid="wellness-support-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSupportModal(false)}>Cancel</Button>
            <Button
              onClick={submitSupportRequest}
              disabled={supportSubmitting || supportReason.trim().length < 5}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="wellness-support-submit"
            >
              {supportSubmitting ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
